import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";

import { resolveDrawingFileUrl } from "apis/procore/bimDrawingDownload";
import { log } from "utils/log";

import type { DrawingImage, DrawingPreview } from "./types";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const l = log.miniMap.utils;

/**
 * The drawing image is rasterized as a `DRAWING_TILE_GRID` x `DRAWING_TILE_GRID`
 * mosaic of PNG tiles. The full-resolution page is never rendered to a single
 * canvas: allocating that canvas (and encoding it to one blob) can exhaust
 * memory and crash the tab. Instead each tile region is rendered directly from
 * the PDF into its own small canvas and encoded independently, then the pieces
 * are recomposed on decode (see `useDrawingImage`).
 */
export const DRAWING_TILE_GRID = 4;

/**
 * Longest-side cap (in pixels) for the single-PNG preview rendered by
 * {@link fetchDrawingData} ahead of the full-resolution tiles. Sized to cover
 * the largest mini map decode (the mobile mini map caps its bitmap at 1536), so
 * both mini maps can decode the preview at their target resolution without ever
 * touching the tiles.
 */
export const DRAWING_PREVIEW_MAX_SIZE = 2048;

export type TileRect = { x: number; y: number; w: number; h: number };

export type FetchDrawingDataOptions = {
    signal?: AbortSignal;
    /**
     * Called as soon as the low-resolution preview is ready, before the
     * (slower) full-resolution tiles are rasterized. Lets consumers display the
     * drawing almost immediately; the returned promise still resolves once the
     * tiles are done. See `fetchDrawingData`.
     */
    onPreview?: (preview: DrawingPreview) => void;
};

/**
 * Compute the pixel rectangles of the `grid` x `grid` tiles that tile an image
 * of the given size, in row-major order (left-to-right, top-to-bottom). Tile
 * boundaries are derived deterministically so encode (`fetchDrawingData`) and
 * decode (`useDrawingImage`) agree, and so the tiles cover the whole image with
 * no gaps or overlaps even when the dimensions aren't divisible by `grid`.
 */
export function getDrawingTileRects(width: number, height: number, grid = DRAWING_TILE_GRID): TileRect[] {
    const rects: TileRect[] = [];
    for (let row = 0; row < grid; row++) {
        const y0 = Math.floor((row * height) / grid);
        const y1 = Math.floor(((row + 1) * height) / grid);
        for (let col = 0; col < grid; col++) {
            const x0 = Math.floor((col * width) / grid);
            const x1 = Math.floor(((col + 1) * width) / grid);
            rects.push({ x: x0, y: y0, w: x1 - x0, h: y1 - y0 });
        }
    }
    return rects;
}

/**
 * Fetch a drawing PDF and rasterize its first page into a *compressed* PNG
 * {@link DrawingImage} in two phases:
 *
 *   1. A single small `preview` PNG of the whole page (longest side capped to
 *      {@link DRAWING_PREVIEW_MAX_SIZE}). One small canvas encodes quickly, so
 *      this is reported via `options.onPreview` almost immediately — the mini
 *      maps only need a downscaled bitmap and can render from it right away.
 *   2. The full-resolution page split into a grid of tiles (for the large map).
 *      This is slow, so it runs after the preview and the returned promise
 *      resolves once it's done.
 *
 * The tiles are intentionally left encoded (a few MB total) rather than decoded
 * into an `ImageBitmap` (~100MB of uncompressed RGBA at the resolution below).
 * Many drawings can be downloaded during a single session, so storing the
 * compressed blobs keeps resident memory small; consumers decode on demand via
 * `useDrawingImage` and release the bitmap when the drawing is no longer
 * displayed. The natural dimensions are captured here (authoritatively, from the
 * render viewport) so consumers never have to decode the full image just to
 * learn its size.
 *
 * The full-resolution page is never rendered to a single canvas. Instead each
 * tile region is rendered directly from the PDF into its own small canvas (via a
 * translation transform on the shared full-page viewport) and encoded on its
 * own, so peak memory stays proportional to a single tile rather than the whole
 * image. See `DRAWING_TILE_GRID`.
 */
export async function fetchDrawingData(url: string, options: FetchDrawingDataOptions = {}): Promise<DrawingImage> {
    const { signal, onPreview } = options;
    l.debug("fetchDrawingData:", url);

    // Scale the image to a large resolution while it's still potentially a
    // vector-based format.
    const imgWidth = 6212;
    const imgHeight = 4800;

    // Document Management sheets need their download endpoint resolved to a signed storage URL
    // first; Drawings-tool sheets already are one. The file itself is then fetched without
    // credentials or custom headers, which is what the storage CORS policy allows.
    const fileUrl = await resolveDrawingFileUrl(url);

    const response = await fetch(fileUrl, { signal });
    if (!response.ok) {
        throw new Error(`Failed to fetch drawing data: HTTP ${response.status} for ${url}`);
    }
    const arrayBuffer = await response.arrayBuffer();
    const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    try {
        const page = await pdfDoc.getPage(1);

        const viewport = page.getViewport({ scale: 1 });
        let scale: number;
        if (viewport.width > viewport.height) {
            scale = imgWidth / viewport.width;
        } else {
            scale = imgHeight / viewport.height;
        }
        const scaledViewport = page.getViewport({ scale: scale });

        const width = Math.round(scaledViewport.width);
        const height = Math.round(scaledViewport.height);

        // Phase 1: render the whole page into a single small canvas. This is
        // cheap (one low-resolution render + one encode) so the mini maps get a
        // usable image well before the full-resolution tiles below are ready.
        // Wrapping into a block hopefully releases canvas memory sooner.
        {
            const previewScale = Math.min(1, DRAWING_PREVIEW_MAX_SIZE / Math.max(width, height));
            const previewWidth = Math.max(1, Math.round(width * previewScale));
            const previewHeight = Math.max(1, Math.round(height * previewScale));
            const previewViewport = page.getViewport({ scale: scale * previewScale });
            const previewCanvas = new OffscreenCanvas(previewWidth, previewHeight);
            const previewContext = previewCanvas.getContext("2d");
            if (!previewContext) {
                throw new Error("Failed to create canvas context");
            }
            await page.render({
                // @ts-expect-error - the type is overly restrictive and does not allow
                // an OffscreenCanvasRenderingContext2D, even though it works in practice
                canvasContext: previewContext,
                viewport: previewViewport,
            }).promise;
            const preview = await previewCanvas.convertToBlob({ type: "image/png" });
            onPreview?.({ preview, width, height });
        }

        // Phase 2: render each tile region straight from the PDF into a small
        // canvas. Rendering the whole page uses the full viewport but is offset
        // by the tile's top-left corner via a translation transform, so only the
        // tile's slice lands inside the canvas.
        const rects = getDrawingTileRects(width, height);
        const tiles: Blob[] = [];
        for (const rect of rects) {
            const tileCanvas = new OffscreenCanvas(rect.w, rect.h);
            const context = tileCanvas.getContext("2d");
            if (!context) {
                throw new Error("Failed to create canvas context");
            }
            const renderContext = {
                canvasContext: context,
                viewport: scaledViewport,
                transform: [1, 0, 0, 1, -rect.x, -rect.y],
            };

            // @ts-expect-error - the type is overly restrictive
            // and does not allow an
            // OffscreenCanvasRenderingContext2D, even though it
            // works in practice
            await page.render(renderContext).promise;
            tiles.push(await tileCanvas.convertToBlob({ type: "image/png" }));
        }

        return { tiles, width, height };
    } finally {
        await pdfDoc.destroy();
    }
}
